import {
  BooleanField,
  BooleanInput,
  Create,
  Datagrid,
  DeleteButton,
  Edit,
  EditButton,
  EditProps,
  FunctionField,
  List,
  ListButton,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  required,
  SelectArrayInput,
  useDataProvider,
  useNotify,
  useRecordContext,
  Button,
} from "react-admin";
import ScrollTopPagination from "./shared/ScrollTopPagination";
import { useFormContext } from "react-hook-form";
import { useState, useEffect } from "react";
import {
  Box,
  Chip,
  Typography,
  Alert,
  FormControl,
  FormLabel,
  FormHelperText,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  Button as MuiButton,
} from "@mui/material";
import {
  Webhook as WebhookIcon,
  ShoppingCart as OrderIcon,
  Person as CustomerIcon,
  AccessTime as SlaIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon,
  ContentCopy as CopyIcon,
  Casino as DiceIcon,
  Send as SendIcon,
  NotificationsActive as AlertIcon,
} from "@mui/icons-material";
import { useAdminTheme, type AdminColorTokens } from "../admin-theme";
import { useLocation } from "react-router-dom";
import Logger from "../utils/Logger";
import config from "../config";
import { getValidToken } from "../services/tokenRefreshService";

// ============================================================================
// Style builders (mode-aware via AdminColorTokens)
// ============================================================================

function buildListSx(c: AdminColorTokens) {
  return {
    ...buildAdminListBase(c),
    '& .MuiInputBase-root': { color: c.textPrimary, bgcolor: c.bgElevated },
    '& .MuiInputLabel-root': { color: c.textSecondary },
    '& .MuiSelect-icon': { color: c.textSecondary },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border },
    '& .MuiMenuItem-root': { color: c.textPrimary },
  };
}

function buildAdminListBase(c: AdminColorTokens) {
  return {
    bgcolor: c.bgDeepest,
    minHeight: '100vh',
    '& .RaList-main': { bgcolor: c.bgDeepest },
    '& .MuiPaper-root': { bgcolor: c.bgCard, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: 2, boxShadow: 'none' },
    '& .MuiToolbar-root': { bgcolor: c.bgCard, color: c.textPrimary },
    '& .MuiTablePagination-root': { color: c.textSecondary, borderTop: `1px solid ${c.border}` },
    '& .MuiTablePagination-selectIcon': { color: c.textSecondary },
    '& .MuiIconButton-root': { color: c.textSecondary },
  };
}

function buildDatagridSx(c: AdminColorTokens) {
  return {
    '& .MuiTableCell-root': { color: c.textPrimary, borderColor: c.border },
    '& .MuiTableHead-root .MuiTableCell-root': {
      color: c.textMuted, fontWeight: 600, textTransform: 'uppercase',
      fontSize: '0.75rem', letterSpacing: '0.05em', bgcolor: c.bgCard,
      borderBottom: `1px solid ${c.border}`,
    },
    '& .MuiTableBody-root .MuiTableRow-root': { bgcolor: c.bgCard },
    '& .MuiTableBody-root .MuiTableRow-root:hover': { bgcolor: `${c.bgElevated} !important` },
    '& .MuiCheckbox-root': { color: c.textMuted },
    '& .MuiButtonBase-root': { color: c.info },
  };
}

function buildFormSx(c: AdminColorTokens) {
  return {
    bgcolor: c.bgDeepest,
    minHeight: '100vh',
    '& .MuiPaper-root': { bgcolor: c.bgCard, color: c.textPrimary, border: `1px solid ${c.border}`, borderRadius: 2, boxShadow: 'none' },
    '& .RaCreate-main, & .RaEdit-main': { bgcolor: c.bgDeepest },
    '& .MuiToolbar-root': { bgcolor: c.bgCard, borderTop: `1px solid ${c.border}` },
    '& .RaToolbar-defaultToolbar': { bgcolor: c.bgCard },
    '& .MuiInputBase-root': { color: c.textPrimary, bgcolor: c.bgElevated },
    '& .MuiInputLabel-root': { color: c.textSecondary },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: c.border },
    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.info },
    '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.info },
    '& .MuiFormHelperText-root': { color: c.textMuted },
    '& .MuiSelect-icon': { color: c.textSecondary },
    '& .MuiSelect-select': { color: c.textPrimary },
    '& .MuiMenuItem-root': { color: c.textPrimary },
    '& .MuiTypography-root': { color: c.textPrimary },
    '& .MuiFormControlLabel-label': { color: c.textPrimary },
    '& .MuiSwitch-track': { bgcolor: c.border },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: `${c.successGlow}60` },
    '& .MuiSwitch-switchBase.Mui-checked': { color: c.successGlow },
    '& .MuiIconButton-root': { color: c.textSecondary },
    '& .MuiButtonBase-root.MuiButton-contained': { bgcolor: c.info, color: '#fff', '&:hover': { bgcolor: `${c.info}CC` } },
    '& .RaSaveButton-button': { bgcolor: `${c.successGlow}20`, color: c.successGlow, border: `1px solid ${c.successGlow}60`, '&:hover': { bgcolor: `${c.successGlow}30`, boxShadow: `0 0 15px ${c.successGlow}30` } },
    '& .MuiChip-root': { bgcolor: `${c.info}15`, color: c.info, border: `1px solid ${c.info}40` },
    '& .MuiChip-deleteIcon': { color: `${c.info}80` },
    '& .MuiAutocomplete-popper .MuiPaper-root': { bgcolor: c.bgElevated, color: c.textPrimary },
  };
}

/**
 * Event definitions per webhook type
 * These match the backend webhook-endpoints/service.js supported events
 */
const EVENT_CONFIG = {
  order: {
    label: "Order Events",
    description: "Triggered when orders are created or status changes",
    icon: <OrderIcon />,
    events: [
      {
        id: "order_initiated",
        label: "Order Initiated",
        description: "When a customer places an order (before payment confirmation)",
      },
      {
        id: "order_created",
        label: "Order Created",
        description: "When payment is confirmed and order is processed",
      },
      {
        id: "order_status_changed",
        label: "Status Changed",
        description: "When order status changes (e.g., dispatched, delivered)",
      },
    ],
  },
  customer: {
    label: "Customer Events",
    description: "Triggered when customers sign up or delete their account",
    icon: <CustomerIcon />,
    events: [
      {
        id: "customer_signed_up",
        label: "Customer Signed Up",
        description: "When a new customer registers",
      },
      {
        id: "customer_deleted",
        label: "Customer Deleted",
        description: "When a customer deletes their account",
      },
    ],
  },
  sla: {
    label: "SLA Events",
    description: "Triggered when orders exceed SLA timing thresholds",
    icon: <AlertIcon />,
    events: [
      {
        id: "picking_sla",
        label: "Picking Delayed",
        description: "Order waiting too long to start picking (PLACED state)",
      },
      {
        id: "packing_sla",
        label: "Packing Delayed",
        description: "Order taking too long in PICKING state",
      },
      {
        id: "pickup_sla",
        label: "Pickup Delayed",
        description: "Packed order waiting too long for courier pickup",
      },
    ],
  },
  error: {
    label: "Error Events",
    description: "Triggered when system errors occur (payments, deliveries, inventory)",
    icon: <ErrorIcon />,
    events: [
      {
        id: "payment_error",
        label: "Payment Error",
        description: "Payment gateway failures (PayGate, Ozow, SnapScan, etc.)",
      },
      {
        id: "uber_direct_error",
        label: "Uber Direct Error",
        description: "Uber Direct delivery or address validation issues",
      },
      {
        id: "inventory_error",
        label: "Inventory Error",
        description: "Vend inventory sync errors",
      },
      {
        id: "general_error",
        label: "General Error",
        description: "Catch-all critical system errors",
      },
    ],
  },
};

const typeChoices = [
  { id: "order", name: "Order Events" },
  { id: "customer", name: "Customer Events" },
  { id: "sla", name: "SLA Events" },
  { id: "error", name: "Error Events" },
];

const formatChoices = [
  { id: "default", name: "Default JSON" },
  { id: "uber", name: "Uber Order V2 Format" },
];

const WebhookFilters = [
  <SelectInput source="type" choices={typeChoices} alwaysOn />,
  <SelectInput
    source="isActive"
    choices={[
      { id: true, name: "Active" },
      { id: false, name: "Inactive" },
    ]}
    alwaysOn
  />,
];

const WebhookPagination = () => (
  <ScrollTopPagination rowsPerPageOptions={[10, 25, 50, 100]} />
);

const listRowStyle = (colors: AdminColorTokens) => (record: any) => ({
  opacity: record?.isActive ? 1 : 0.6,
  bgcolor: colors.bgCard,
});

const validateUrl = (value: string) => {
  if (!value) return "URL is required";
  try {
    new URL(value);
    return undefined;
  } catch (err) {
    return "Must be a valid URL";
  }
};

/**
 * Detect if URL is a Discord webhook
 */
const isDiscordUrl = (url: string) => {
  return url?.includes("discord.com/api/webhooks/");
};

/**
 * Event Chips Display for List view
 */
const EventChips = ({ record }: { record: any }) => {
  const { colors } = useAdminTheme();
  const events = record?.events || [];
  const type = record?.type;
  const config = EVENT_CONFIG[type as keyof typeof EVENT_CONFIG];

  if (!events.length) {
    return (
      <Chip
        label="All Events"
        size="small"
        sx={{
          bgcolor: `${colors.info}15`,
          color: colors.info,
          border: `1px solid ${colors.info}40`,
          fontWeight: 600,
        }}
      />
    );
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
      {events.map((event: string) => {
        const eventConfig = config?.events.find((e) => e.id === event);
        return (
          <Chip
            key={event}
            label={eventConfig?.label || event}
            size="small"
            sx={{
              bgcolor: `${colors.successGlow}15`,
              color: colors.successGlow,
              border: `1px solid ${colors.successGlow}40`,
              fontWeight: 600,
            }}
          />
        );
      })}
    </Stack>
  );
};

/**
 * URL Field with Discord indicator
 */
const UrlField = ({ record }: { record: any }) => {
  const { colors } = useAdminTheme();
  const url = record?.url || "";
  const isDiscord = isDiscordUrl(url);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography
        variant="body2"
        sx={{
          maxWidth: 300,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          color: colors.textSecondary,
          fontFamily: 'monospace',
          fontSize: '0.8rem',
        }}
      >
        {url}
      </Typography>
      {isDiscord && (
        <Chip
          label="Discord"
          size="small"
          sx={{
            bgcolor: '#5865F215',
            color: '#5865F2',
            border: '1px solid #5865F240',
            fontWeight: 600,
          }}
        />
      )}
    </Box>
  );
};

/**
 * Interactive Event Selector with toggleable chips
 */
const EventSelector = () => {
  const { colors } = useAdminTheme();
  const { setValue, watch } = useFormContext();
  const type = watch("type") as keyof typeof EVENT_CONFIG;
  const config = EVENT_CONFIG[type];
  const events = watch("events") || [];


  if (!config) {
    Logger.warn("[EventSelector] No config found for type:", type);
    return null;
  }

  const selectedEvents: string[] = Array.isArray(events) ? events : [];
  const allSelected =
    selectedEvents.length === 0 ||
    selectedEvents.length === config.events.length;



  const toggleEvent = (eventId: string) => {
    if (selectedEvents.length === 0) {
      // Switching from "All Events" to individual — select ONLY the clicked one
      setValue("events", [eventId], { shouldDirty: true });
    } else if (selectedEvents.includes(eventId)) {
      // Remove event
      const newEvents = selectedEvents.filter((e) => e !== eventId);
      setValue("events", newEvents, { shouldDirty: true });
    } else {
      // Add event
      setValue("events", [...selectedEvents, eventId], { shouldDirty: true });
    }
  };

  const selectAll = () => {
    setValue("events", [], { shouldDirty: true });
  };

  return (
    <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
      <FormLabel component="legend" sx={{ mb: 1, color: colors.textSecondary }}>
        Events to Subscribe
      </FormLabel>

      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
        {/* All Events chip */}
        <Chip
          label="All Events"
          icon={allSelected ? <CheckIcon /> : undefined}
          onClick={selectAll}
          sx={{
            fontWeight: allSelected ? 700 : 400,
            bgcolor: allSelected ? `${colors.info}20` : 'transparent',
            color: allSelected ? colors.info : colors.textSecondary,
            border: `1px solid ${allSelected ? colors.info : colors.border}`,
            '& .MuiChip-icon': { color: colors.info },
          }}
        />

        {/* Individual event chips */}
        {config.events.map((event) => {
          const isSelected = selectedEvents.includes(event.id);

          return (
            <Chip
              key={event.id}
              label={event.label}
              icon={isSelected ? <CheckIcon /> : undefined}
              onClick={() => toggleEvent(event.id)}
              title={event.description}
              sx={{
                cursor: 'pointer',
                bgcolor: isSelected ? `${colors.successGlow}20` : 'transparent',
                color: isSelected ? colors.successGlow : colors.textSecondary,
                border: `1px solid ${isSelected ? colors.successGlow : colors.border}`,
                '& .MuiChip-icon': { color: colors.successGlow },
              }}
            />
          );
        })}
      </Stack>

      <FormHelperText sx={{ mt: 1, color: colors.textMuted }}>
        {allSelected && selectedEvents.length === 0
          ? "Receiving all events for this type"
          : `Receiving ${selectedEvents.length} of ${config.events.length} events`}
      </FormHelperText>
    </FormControl>
  );
};

/**
 * URL Input with Discord detection and raw payload options
 */
const UrlInput = () => {
  const { watch, setValue } = useFormContext();
  const url = watch("url") || "";
  const isDiscord = isDiscordUrl(url);
  const includeRawFile = watch("includeRawFile") || false;
  const rawFileFormat = watch("rawFileFormat") || "default";

  return (
    <Box sx={{ width: "100%" }}>
      <TextInput
        source="url"
        label="Webhook URL"
        validate={[required(), validateUrl]}
        fullWidth
        helperText="The URL that will receive POST requests with event data"
      />

      {isDiscord && (
        <Alert
          severity="success"
          sx={{
            mt: 1,
            mb: 2,
            bgcolor: 'success.dark',
            color: 'success.contrastText',
            '& .MuiAlert-icon': {
              color: 'success.contrastText'
            }
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1, color: 'inherit' }}>
            🎉 Discord webhook detected!
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'inherit' }}>
            Payloads will automatically be formatted as rich embeds with colors and emojis.
          </Typography>

          <Box sx={{
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            p: 2,
            borderRadius: 1,
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}>
            <BooleanInput
              source="includeRawFile"
              label="Include raw payload as text file attachment"
              helperText="Attach the full JSON payload as a .txt file to avoid Discord's embed truncation"
              sx={{
                '& .MuiFormControlLabel-label': {
                  color: 'inherit'
                },
                '& .MuiFormHelperText-root': {
                  color: 'rgba(255, 255, 255, 0.9)'
                }
              }}
            />

            {includeRawFile && (
              <Box sx={{ ml: 0, mt: 2 }}>
                <SelectInput
                  source="rawFileFormat"
                  label="Raw File Format"
                  choices={[
                    { id: "default", name: "Default JSON" },
                    { id: "uber", name: "Uber Order V2 Format" },
                  ]}
                  defaultValue="default"
                  helperText="Choose which format to use for the attached file"
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    },
                    '& .MuiFormHelperText-root': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        </Alert>
      )}
    </Box>
  );
};

/**
 * Type Selector with descriptions
 */
const TypeSelector = () => {
  const { colors } = useAdminTheme();
  const { watch } = useFormContext();
  const type = watch("type") as keyof typeof EVENT_CONFIG;
  const config = EVENT_CONFIG[type];

  return (
    <Box sx={{ width: "100%" }}>
      <SelectInput
        source="type"
        label="Webhook Type"
        choices={typeChoices}
        validate={[required()]}
        helperText={config?.description || "Select the type of events to receive"}
        fullWidth
      />

      {type === "customer" && (
        <Alert severity="success" sx={{ mt: 1, bgcolor: `${colors.successGlow}10`, border: `1px solid ${colors.successGlow}40`, borderRadius: 2, color: colors.textPrimary, '& .MuiAlert-icon': { color: colors.successGlow } }}>
          <strong>Customer Events Selected</strong>
          <Typography variant="body2" sx={{ mt: 0.5, color: colors.textSecondary }}>
            You'll receive notifications when new customers sign up or delete their account.
          </Typography>
        </Alert>
      )}

      {type === "order" && (
        <Alert severity="info" sx={{ mt: 1, bgcolor: `${colors.info}10`, border: `1px solid ${colors.info}40`, borderRadius: 2, color: colors.textPrimary, '& .MuiAlert-icon': { color: colors.info } }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            <strong style={{ color: colors.textPrimary }}>Tip:</strong> For customer signup notifications, select "Customer Events" type instead.
          </Typography>
        </Alert>
      )}

      {type === "sla" && (
        <Alert severity="warning" sx={{ mt: 1, bgcolor: `${colors.warning}10`, border: `1px solid ${colors.warning}40`, borderRadius: 2, color: colors.textPrimary, '& .MuiAlert-icon': { color: colors.warning } }}>
          <strong>SLA Events Selected</strong>
          <Typography variant="body2" sx={{ mt: 0.5, color: colors.textSecondary }}>
            You'll receive notifications when orders exceed SLA timing thresholds.
            Configure SLA timing in Store Defaults → Order Operations.
          </Typography>
        </Alert>
      )}

      {type === "error" && (
        <Alert severity="error" sx={{ mt: 1, bgcolor: `${colors.errorGlow}10`, border: `1px solid ${colors.errorGlow}40`, borderRadius: 2, color: colors.textPrimary, '& .MuiAlert-icon': { color: colors.errorGlow } }}>
          <strong>Error Events Selected</strong>
          <Typography variant="body2" sx={{ mt: 0.5, color: colors.textSecondary }}>
            You'll receive notifications when system errors occur, including:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ mt: 0.5, mb: 0, pl: 2, color: colors.textSecondary }}>
            <li>Payment failures (PayGate, Ozow, SnapScan)</li>
            <li>Uber Direct delivery/address issues</li>
            <li>Inventory sync errors (Vend)</li>
            <li>Critical system errors</li>
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

/**
 * Format Selector - Hidden when Discord webhook is detected
 */
const FormatSelector = () => {
  const { colors } = useAdminTheme();
  const { watch } = useFormContext();
  const format = watch("format") || "default";
  const url = watch("url") || "";
  const isDiscord = isDiscordUrl(url);

  // Hide format selector when Discord is detected (auto-formats regardless)
  if (isDiscord) {
    return null;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <SelectInput
        source="format"
        label="Webhook Format"
        choices={formatChoices}
        defaultValue="default"
        helperText={
          format === "uber"
            ? "Sends Uber Order V2 format with resource_href pattern"
            : "Sends standard JSON payload"
        }
        fullWidth
      />
      {format === "uber" && (
        <Alert severity="info" sx={{ mt: 1, bgcolor: `${colors.info}10`, border: `1px solid ${colors.info}40`, borderRadius: 2, color: colors.textPrimary, '& .MuiAlert-icon': { color: colors.info } }}>
          <strong>Uber Format:</strong> Webhooks will include resource_href for order details and HMAC signatures if signing key is set.
        </Alert>
      )}
    </Box>
  );
};

/**
 * Signing Key Input with generator and copy
 */
const SigningKeyInput = () => {
  const { setValue, watch } = useFormContext();
  const notify = useNotify();
  const signingKey = watch("signingKey") || "";

  const generateKey = () => {
    // Generate a secure random key (32 bytes = 64 hex chars)
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const key = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
    setValue("signingKey", key, { shouldDirty: true });
    notify("Signing key generated", { type: "success" });
  };

  const copyKey = () => {
    navigator.clipboard.writeText(signingKey);
    notify("Signing key copied to clipboard", { type: "success" });
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextInput
          source="signingKey"
          label="HMAC Signing Key"
          helperText="Secret key for HMAC-SHA256 webhook signatures (optional, for Uber format)"
          fullWidth
          type="password"
        />
        <Box sx={{ mt: 1, display: "flex", gap: 0.5 }}>
          <Tooltip title="Generate random key">
            <IconButton onClick={generateKey} size="small">
              <DiceIcon />
            </IconButton>
          </Tooltip>
          {signingKey && (
            <Tooltip title="Copy key">
              <IconButton onClick={copyKey} size="small">
                <CopyIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

/**
 * Store Exclusion Selector
 */
const StoreExclusionSelector = () => {
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { watch, setValue } = useFormContext();
  const dataProvider = useDataProvider();

  // Ensure excludedStoreIds is always an array BEFORE rendering
  const excludedStoreIds = watch("excludedStoreIds");

  // Initialize as empty array if null/undefined
  useEffect(() => {
    if (!Array.isArray(excludedStoreIds)) {
      setValue("excludedStoreIds", [], { shouldDirty: false });
    }
  }, []); // Run once on mount

  useEffect(() => {
    // Fetch stores from admin API endpoint
    const apiUrl = import.meta.env.VITE_API_URL || config.API_URL.replace(/\/v1$/, '');

    (async () => {
      const token = await getValidToken();

      if (!token) {
        Logger.error("[StoreExclusionSelector] No auth token found");
        setLoading(false);
        return;
      }

      fetch(`${apiUrl}/admin/stores`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
          });
        }
        return response.json();
      })
      .then((data) => {
        // Admin stores endpoint returns array directly
        const storesList = Array.isArray(data) ? data : [];
        // Use externalId as the value - this is what order.storeId references
        // The webhook service filters by storeId which is the externalId, not the UUID
        const storeChoices = storesList
          .filter((store: any) => store.externalId) // Skip stores without externalId
          .map((store: any) => ({
            id: store.externalId,
            name: store.name || store.externalId || "Unnamed Store",
          }));
        setStores(storeChoices);
        setLoading(false);
      })
      .catch((error: any) => {
        Logger.error("[StoreExclusionSelector] Fetch failed:", error.message);
        setLoading(false);
      });
    })();
  }, []);

  // Don't render SelectArrayInput until we have array value
  if (!Array.isArray(excludedStoreIds)) {
    return null;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <SelectArrayInput
        source="excludedStoreIds"
        label="Excluded Stores"
        choices={stores}
        optionText="name"
        optionValue="id"
        helperText="Select stores that should NOT receive webhooks"
        fullWidth
        disabled={loading}
      />
      {loading && <FormHelperText>Loading stores...</FormHelperText>}
      {!loading && stores.length === 0 && (
        <FormHelperText>No stores found in database</FormHelperText>
      )}
    </Box>
  );
};

/**
 * Webhook Form Content - shared between Create and Edit
 */
const WebhookFormContent = () => {
  const { colors } = useAdminTheme();
  return (
    <Stack spacing={2} sx={{ width: "100%", maxWidth: 600 }}>
      <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, color: colors.textPrimary, fontWeight: 700 }}>
        <WebhookIcon sx={{ color: colors.info }} /> Webhook Configuration
      </Typography>

      <TextInput
        source="name"
        label="Webhook Name"
        helperText="Optional friendly name to identify this webhook"
        fullWidth
      />

      <TypeSelector />
      <UrlInput />
      <FormatSelector />
      <EventSelector />
      <SigningKeyInput />
      <StoreExclusionSelector />

      <BooleanInput
        source="isActive"
        label="Active"
        helperText="Inactive webhooks won't receive any events"
      />
    </Stack>
  );
};

// ============ EXPORTS ============

export const WebhookEndpointList = (props: any) => {
  const { colors } = useAdminTheme();
  return (
    <List
      {...props}
      filters={WebhookFilters}
      pagination={<WebhookPagination />}
      perPage={25}
      title="Webhook Endpoints"
      filter={{ source: 'internal' }}
      sx={buildListSx(colors)}
    >
      <Datagrid
        rowStyle={listRowStyle(colors)}
        bulkActionButtons={false}
        rowClick={(id, resource, record) =>
          `/${resource}/${id}?record=${encodeURIComponent(
            JSON.stringify(record)
          )}`
        }
        sx={buildDatagridSx(colors)}
      >
        <FunctionField
          label="Name"
          render={(record: any) => (
            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
              {record?.name || <em style={{ color: colors.textMuted }}>Unnamed</em>}
            </Typography>
          )}
        />
        <FunctionField label="URL" render={(record: any) => <UrlField record={record} />} />
        <FunctionField
          label="Type"
          render={(record: any) => {
            const config = EVENT_CONFIG[record?.type as keyof typeof EVENT_CONFIG];
            return (
              <Chip
                icon={config?.icon}
                label={config?.label || record?.type}
                size="small"
                sx={{
                  bgcolor: `${colors.info}15`,
                  color: colors.info,
                  border: `1px solid ${colors.info}40`,
                  '& .MuiChip-icon': { color: colors.info },
                }}
              />
            );
          }}
        />
        <FunctionField
          label="Events"
          render={(record: any) => <EventChips record={record} />}
        />
        <BooleanField source="isActive" label="Active" />
        <EditButton />
        <DeleteButton />
      </Datagrid>
    </List>
  );
};

/**
 * Transform form data before sending to API
 * - Removes client-only fields that aren't in the backend schema
 * - Sets source='internal' to mark webhook as created from Admin page
 */
const transformWebhookData = (data: any) => {
  return {
    ...data,
    source: 'internal', // set by this page; other sources exist on the endpoint record
  };
};

export const WebhookEndpointCreate = (props: any) => {
  const { colors } = useAdminTheme();
  return (
  <Create {...props} redirect="list" title="Create Webhook Endpoint" transform={transformWebhookData} sx={buildFormSx(colors)}>
    <SimpleForm
      defaultValues={{
        type: "order",
        format: "default",
        isActive: true,
        events: [],
        excludedStoreIds: [],
        includeRawFile: false,
        rawFileFormat: "default",
      }}
    >
      <WebhookFormContent />
    </SimpleForm>
  </Create>
  );
};

/**
 * Test Webhook Button - sends test order webhook with status selection
 */
const TestWebhookButton = () => {
  const { colors } = useAdminTheme();
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orderStatus, setOrderStatus] = useState("placed");

  const orderStatuses = [
    { id: "placed", name: "Placed" },
    { id: "packed", name: "Packed" },
    { id: "dispatched", name: "Dispatched" },
    { id: "tracking", name: "Tracking" },
    { id: "arrived", name: "Arrived" },
    { id: "delivered", name: "Delivered" },
    { id: "cancelled", name: "Cancelled" },
    { id: "returned", name: "Returned" },
  ];

  const handleTest = async () => {
    if (!record?.id) return;

    setLoading(true);
    setDialogOpen(false);
    try {
      const response = await dataProvider.create(`webhook-endpoints/${record.id}/test`, {
        data: { orderStatus },
      });

      if (response.data.status === "success") {
        notify(`Test webhook sent successfully with status: ${orderStatus}`, { type: "success" });
      } else {
        notify(`Test failed: ${response.data.message}`, { type: "warning" });
      }
    } catch (error: any) {
      notify(`Error: ${error.message}`, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (!record?.id) return null;

  return (
    <>
      <Button
        label={loading ? "Sending..." : "Send Test Webhook"}
        onClick={() => setDialogOpen(true)}
        disabled={loading || !record?.isActive}
        startIcon={<SendIcon />}
        variant="contained"
        color="primary"
        sx={{ mr: 1 }}
      />
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: colors.bgCard,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
            color: colors.textPrimary,
          },
        }}
      >
        <DialogTitle sx={{ color: colors.textPrimary, fontWeight: 700 }}>Send Test Webhook</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel sx={{ color: colors.textSecondary }}>Order Status</InputLabel>
            <Select
              value={orderStatus}
              label="Order Status"
              onChange={(e) => setOrderStatus(e.target.value)}
              sx={{
                color: colors.textPrimary,
                bgcolor: colors.bgElevated,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.info },
                '& .MuiSelect-icon': { color: colors.textSecondary },
              }}
              MenuProps={{
                PaperProps: { sx: { bgcolor: colors.bgElevated, color: colors.textPrimary, border: `1px solid ${colors.border}` } },
              }}
            >
              {orderStatuses.map((status) => (
                <MenuItem key={status.id} value={status.id} sx={{ '&:hover': { bgcolor: colors.bgCard } }}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${colors.border}`, px: 3, py: 2 }}>
          <MuiButton onClick={() => setDialogOpen(false)} sx={{ color: colors.textSecondary }}>Cancel</MuiButton>
          <MuiButton
            onClick={handleTest}
            variant="outlined"
            sx={{
              color: colors.info,
              borderColor: `${colors.info}60`,
              bgcolor: `${colors.info}10`,
              fontWeight: 600,
              '&:hover': { bgcolor: `${colors.info}20`, borderColor: colors.info, boxShadow: `0 0 15px ${colors.info}30` },
            }}
          >
            Send Test
          </MuiButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

/**
 * Custom Edit Toolbar with Back and Test Button
 */
const WebhookEditToolbar = (props: any) => {
  const { colors } = useAdminTheme();
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, p: 2, bgcolor: colors.bgCard, borderRadius: 2, border: `1px solid ${colors.border}` }}>
      <ListButton label="Back to List" icon={<Box component="span">←</Box>} sx={{ color: colors.textSecondary }} />
      <Box sx={{ flex: 1 }} />
      <TestWebhookButton />
    </Stack>
  );
};

export const WebhookEndpointEdit = (props: EditProps) => {
  const { colors } = useAdminTheme();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const recordFromQuery = searchParams.get("record");
  const recordFromList =
    (location.state as any)?.record ||
    (recordFromQuery ? JSON.parse(decodeURIComponent(recordFromQuery)) : null);

  return (
    <Edit
      {...props}
      title="Edit Webhook Endpoint"
      transform={transformWebhookData}
      queryOptions={{
        enabled: !recordFromList,
        initialData: recordFromList,
      }}
      sx={buildFormSx(colors)}
    >
      <Stack spacing={2}>
        <WebhookEditToolbar />
        <SimpleForm defaultValues={{ events: [], excludedStoreIds: [], includeRawFile: false, rawFileFormat: "default" }}>
          <WebhookFormContent />
        </SimpleForm>
      </Stack>
    </Edit>
  );
};
